import React, { useMemo } from 'react';

import {
  InteractiveElement,
  useSankeyInteractionContext,
} from '../../contexts/sankeyInteractionContext';
import { useSankeyRenderContext } from '../../contexts/sankeyRenderContext';
import { SankeyFlow, SankeyNode } from '../../diagram/types';

export const useSankeySVGInteraction = () => {
  const {
    draggedNode,
    isDiagramInteractive,
    isElementFaded,
    handleMouseOut,
    handleNodeDragMove,
    handleNodeDragEnd,
    setHintText,
  } = useSankeyInteractionContext();

  return {
    draggedNode,
    isDiagramInteractive,
    isElementFaded,
    handleMouseOver: () => {
      setHintText('Click on any element to edit it');
    },
    handleMouseMove: (ev: React.MouseEvent<SVGElement>) => {
      handleNodeDragMove(ev);
    },
    handleMouseLeave: () => {
      setHintText('');
      handleNodeDragEnd();
      handleMouseOut();
    },
    handleMouseUp: handleNodeDragEnd,
  };
};

export const useSankeyTitleInteraction = () => {
  const interactiveElement = useMemo(
    (): InteractiveElement => ({ type: 'title' }),
    []
  );

  const {
    isElementHighlighted,
    isElementFaded,
    isElementFocused,
    focusElement,
    blurFocusedElement,
    handleMouseOver,
    handleMouseOut,
    setHintText,
  } = useSankeyInteractionContext();

  return {
    isHighlighted: isElementHighlighted(interactiveElement),
    isFaded: isElementFaded(interactiveElement),
    isFocused: isElementFocused(interactiveElement),
    handleFocus: () => focusElement(interactiveElement),
    handleBlur: () => blurFocusedElement(),
    handleMouseOver: (ev: React.MouseEvent<SVGElement>) => {
      handleMouseOver(interactiveElement);
      setHintText('Click to edit the title');
      ev.stopPropagation();
    },
    handleMouseOut,
  };
};

export const useSankeyNodeInteraction = (node: SankeyNode) => {
  const interactiveElement = useMemo(
    (): InteractiveElement => ({
      type: 'node',
      id: node.id,
    }),
    [node.id]
  );

  const { moveNodeTo, saveNodeMove } = useSankeyRenderContext();
  const {
    isElementFocused,
    isElementHighlighted,
    focusElement,
    blurFocusedElement,
    handleMouseOver,
    handleMouseOut,
    setHintText,
    handleNodeDragStart,
  } = useSankeyInteractionContext();

  return {
    isFocused: isElementFocused(interactiveElement),
    isHighlighted: isElementHighlighted(interactiveElement),
    handleFocus: () => focusElement(interactiveElement),
    handleBlur: () => blurFocusedElement(),
    handleMouseOver: (ev: React.MouseEvent<SVGElement>) => {
      handleMouseOver(interactiveElement);
      setHintText(
        'Click to edit, drag to move and double-click to reset position'
      );
      ev.stopPropagation();
    },
    handleMouseOut,
    handleMouseDown: (ev: React.MouseEvent<SVGElement, MouseEvent>) => {
      handleNodeDragStart(ev, node);
    },
    handleDoubleClick: () => {
      moveNodeTo(node, [0, 0]);
      saveNodeMove(node);
    },
    setHintText,
  };
};

export const useSankeyFlowInteraction = (flow: SankeyFlow) => {
  const interactiveElement = useMemo(
    (): InteractiveElement => ({
      type: 'flow',
      id: flow.id,
    }),
    [flow.id]
  );

  const {
    isElementFocused,
    focusElement,
    blurFocusedElement,
    handleMouseOver,
    handleMouseOut,
    setHintText,
  } = useSankeyInteractionContext();

  return {
    isFocused: isElementFocused(interactiveElement),
    handleFocus: () => focusElement(interactiveElement),
    handleBlur: () => blurFocusedElement(),
    handleMouseOver: (ev: React.MouseEvent<SVGElement>) => {
      handleMouseOver(interactiveElement);
      setHintText('Click to edit this flow');
      ev.stopPropagation();
    },
    handleMouseOut,
  };
};
